/**
 * landing-ceremonies.tsx — "As cerimônias" section for the public landing page.
 *
 * The real app celebrates progress with ~92 full-screen "ceremony" scenes
 * (see src/components/moments*.tsx): a candle that lights up when the daily
 * goal is hit, a constellation that draws itself across a week of writing,
 * a character dossier that gets typed out and stamped, a level-up burst of
 * sparks. Visitors never see any of that on the marketing site today.
 *
 * This component recreates LIGHTWEIGHT, self-contained versions of four of
 * those ceremonies purely in CSS/motion (no MomentsWatcher import, no app
 * state) so the landing page can show — not just claim — the product's
 * night-cinematic identity in the first scroll.
 *
 * Each mini-scene plays its entrance once it scrolls into view, then loops
 * gently forever (ignite → hold → fade → re-ignite) via Framer Motion's
 * `repeatType: 'mirror'`, so the section keeps feeling alive while visitors
 * read the copy. `useReducedMotion` swaps every scene for a static "settled"
 * frame — no motion, no repeat, no flicker.
 */
import type { ReactNode } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { useTranslation } from 'react-i18next';

// ---------------------------------------------------------------------------
// Shared stage dressing — faint static dust behind every mini-scene, same
// deep-night "casca" gradient the real app uses for its ceremony overlays.
// ---------------------------------------------------------------------------
const STAGE_DUST: ReadonlyArray<readonly [number, number, number]> = [
  [8, 20, 2], [22, 68, 1.5], [38, 14, 1.5], [54, 80, 2], [68, 30, 1.5],
  [82, 55, 2], [91, 12, 1.5], [15, 45, 1.5], [47, 92, 1.5], [76, 88, 2],
];

function CeremonyFrame({
  index, eyebrow, title, caption, children,
}: {
  index: number;
  eyebrow: string;
  title: string;
  caption: string;
  children: ReactNode;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28, scale: 0.94 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.35 }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="ceremony-card group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
    >
      <span className="ceremony-sheen" aria-hidden />

      {/* Stage — always the deep-night casca, true to how the ceremony renders in-app */}
      <div
        className="relative flex h-[168px] items-center justify-center overflow-hidden sm:h-[188px]"
        style={{
          background:
            'radial-gradient(ellipse 90% 70% at 50% 20%, rgba(146,164,214,0.16), transparent 65%), linear-gradient(180deg, #0C1326 0%, #080D1B 100%)',
        }}
      >
        <div className="pointer-events-none absolute inset-0 opacity-70" aria-hidden>
          {STAGE_DUST.map(([x, y, s], i) => (
            <span
              key={i}
              className="absolute rounded-full"
              style={{ left: `${x}%`, top: `${y}%`, width: s, height: s, background: 'rgba(168,184,232,0.5)' }}
            />
          ))}
        </div>
        {children}
      </div>

      {/* Caption */}
      <div className="p-5">
        <p className="font-mono text-[9px] uppercase tracking-[0.26em] text-accent">✦ {eyebrow}</p>
        <h3 className="mt-1.5 font-serif text-base font-semibold text-foreground sm:text-lg">{title}</h3>
        <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground sm:text-sm">{caption}</p>
      </div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// 1 — the candle lights (daily goal hit)
// ---------------------------------------------------------------------------
function CandleScene({ reduced }: { reduced: boolean }) {
  if (reduced) {
    return (
      <div style={{ transform: 'scale(2.2)' }}>
        <span className="candle-mini" aria-hidden>
          <span className="fl" style={{ position: 'absolute', animation: 'none', opacity: 0.9 }} />
          <span className="wk" />
          <span className="wx" />
        </span>
      </div>
    );
  }
  return (
    <motion.div
      style={{ transformOrigin: 'center bottom' }}
      initial={{ scale: 2.2, y: 14, opacity: 0 }}
      whileInView={{ scale: 2.2, y: 0, opacity: 1 }}
      viewport={{ once: true, amount: 0.6 }}
      transition={{
        type: 'spring', stiffness: 130, damping: 15,
        repeat: Infinity, repeatType: 'mirror', repeatDelay: 2.4,
      }}
    >
      <span className="candle-mini" aria-hidden>
        <span className="fl" style={{ position: 'absolute' }} />
        <span className="wk" />
        <span className="wx" />
      </span>
      <motion.span
        aria-hidden
        className="pointer-events-none absolute -inset-10 rounded-full"
        style={{ background: 'radial-gradient(circle at 50% 22%, rgba(246,233,200,0.4), transparent 62%)' }}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 0.7 }}
        viewport={{ once: true, amount: 0.6 }}
        transition={{ delay: 0.3, duration: 1, repeat: Infinity, repeatType: 'mirror', repeatDelay: 2.6 }}
      />
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// 2 — the week's constellation draws itself
// ---------------------------------------------------------------------------
const CONST_POINTS: ReadonlyArray<readonly [number, number]> = [
  [20, 74], [46, 42], [72, 58], [98, 24], [124, 46], [150, 20], [176, 50],
];
const CONST_PATH = CONST_POINTS.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x},${y}`).join(' ');

function ConstellationScene({ reduced }: { reduced: boolean }) {
  if (reduced) {
    return (
      <svg viewBox="0 0 196 96" className="h-[86px] w-[172px]" aria-hidden>
        <path d={CONST_PATH} fill="none" className="const-line" strokeWidth={1.4} />
        {CONST_POINTS.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={2.4} fill="#F6E9C8" />
        ))}
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 196 96" className="h-[86px] w-[172px]" aria-hidden>
      <motion.path
        d={CONST_PATH}
        fill="none"
        className="const-line"
        strokeWidth={1.4}
        initial={{ pathLength: 0, opacity: 0 }}
        whileInView={{ pathLength: 1, opacity: 1 }}
        viewport={{ once: true, amount: 0.6 }}
        transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1], repeat: Infinity, repeatType: 'mirror', repeatDelay: 1.6 }}
      />
      {CONST_POINTS.map(([x, y], i) => (
        <motion.circle
          key={i}
          cx={x}
          cy={y}
          r={2.6}
          fill="#F6E9C8"
          style={{ filter: 'drop-shadow(0 0 4px rgba(246,233,200,0.85))' }}
          initial={{ scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{
            delay: (i / CONST_POINTS.length) * 1.6,
            type: 'spring', stiffness: 260, damping: 14,
            repeat: Infinity, repeatType: 'mirror',
            repeatDelay: 3.2 - (i / CONST_POINTS.length) * 1.6,
          }}
        />
      ))}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// 3 — a character's dossier gets typed out and stamped
// ---------------------------------------------------------------------------
function DossierScene({ reduced, name, stamp }: { reduced: boolean; name: string; stamp: string }) {
  const lines = [78, 60, 68];
  const paperStyle = { background: 'linear-gradient(180deg,#F6F1E4,#EAE2CE)', boxShadow: '0 18px 40px -12px rgba(0,0,0,0.55)' };
  const stampStyle = {
    color: '#8A3B2E',
    borderColor: 'rgba(138,59,46,0.75)',
    background: 'rgba(246,241,228,0.92)',
  };

  if (reduced) {
    return (
      <div className="relative w-[152px] rounded-[3px_7px_7px_3px] px-4 pb-9 pt-4" style={paperStyle}>
        <p className="font-mono text-[6.5px] uppercase tracking-[0.24em]" style={{ color: '#A8935F' }}>dossiê</p>
        <p className="mt-1.5 font-serif text-[14px] font-medium leading-tight" style={{ color: '#241C36' }}>{name}</p>
        <div className="mt-2.5 space-y-2">
          {lines.map((w, i) => (
            <span key={i} className="block h-[4px] rounded-full" style={{ width: `${w}%`, background: 'rgba(70,55,30,0.16)' }} />
          ))}
        </div>
        <span
          className="absolute bottom-2.5 right-3 rounded-[3px] border-2 px-2 py-0.5 font-mono text-[7.5px] font-bold uppercase tracking-[0.14em]"
          style={{ ...stampStyle, transform: 'rotate(-9deg)' }}
        >
          {stamp}
        </span>
      </div>
    );
  }

  return (
    <motion.div
      className="relative w-[152px] rounded-[3px_7px_7px_3px] px-4 pb-9 pt-4"
      style={paperStyle}
      initial={{ y: 20, rotate: 3, opacity: 0 }}
      whileInView={{ y: 0, rotate: -1, opacity: 1 }}
      viewport={{ once: true, amount: 0.6 }}
      transition={{ type: 'spring', stiffness: 150, damping: 16, repeat: Infinity, repeatType: 'mirror', repeatDelay: 2.6 }}
    >
      <p className="font-mono text-[6.5px] uppercase tracking-[0.24em]" style={{ color: '#A8935F' }}>dossiê</p>
      <div className="mt-1.5 overflow-hidden">
        <motion.p
          className="whitespace-nowrap font-serif text-[14px] font-medium leading-tight"
          style={{ color: '#241C36' }}
          initial={{ width: '0%' }}
          whileInView={{ width: '100%' }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ delay: 0.3, duration: 0.75, ease: 'linear', repeat: Infinity, repeatType: 'mirror', repeatDelay: 3.1 }}
        >
          {name}
        </motion.p>
      </div>
      <div className="mt-2.5 space-y-2">
        {lines.map((w, i) => (
          <motion.span
            key={i}
            aria-hidden
            className="block h-[4px] rounded-full"
            style={{ background: 'rgba(70,55,30,0.16)' }}
            initial={{ width: '0%', opacity: 0 }}
            whileInView={{ width: `${w}%`, opacity: 1 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ delay: 0.9 + i * 0.14, duration: 0.4, repeat: Infinity, repeatType: 'mirror', repeatDelay: 3.4 - i * 0.1 }}
          />
        ))}
      </div>
      <motion.span
        aria-hidden
        className="absolute bottom-2.5 right-3 rounded-[3px] border-2 px-2 py-0.5 font-mono text-[7.5px] font-bold uppercase tracking-[0.14em]"
        style={stampStyle}
        initial={{ scale: 2.2, opacity: 0, rotate: 6 }}
        whileInView={{ scale: 1, opacity: 1, rotate: -9 }}
        viewport={{ once: true, amount: 0.6 }}
        transition={{ delay: 1.5, type: 'spring', stiffness: 340, damping: 14, repeat: Infinity, repeatType: 'mirror', repeatDelay: 2.6 }}
      >
        {stamp}
      </motion.span>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// 4 — level up, sparks fly
// ---------------------------------------------------------------------------
const SPARKS: ReadonlyArray<{ x: number; y: number; d: number }> = [
  { x: -46, y: -30, d: 0.0 }, { x: 42, y: -38, d: 0.1 }, { x: -26, y: -54, d: 0.18 },
  { x: 58, y: -14, d: 0.26 }, { x: -60, y: -6, d: 0.32 }, { x: 20, y: -60, d: 0.4 },
];

function LevelUpScene({ reduced }: { reduced: boolean }) {
  const C = 2 * Math.PI * 34;

  if (reduced) {
    return (
      <div className="relative h-[92px] w-[92px]">
        <svg viewBox="0 0 84 84" className="h-full w-full -rotate-90">
          <circle cx="42" cy="42" r="34" fill="none" stroke="rgba(168,184,232,0.16)" strokeWidth="5" />
          <circle cx="42" cy="42" r="34" fill="none" stroke="#F6E9C8" strokeWidth="5" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * 0.18} />
        </svg>
        <span className="absolute inset-0 grid place-items-center font-serif text-[30px] font-semibold" style={{ color: '#DFC993' }}>7</span>
      </div>
    );
  }

  return (
    <div className="relative h-[92px] w-[92px]">
      <svg viewBox="0 0 84 84" className="h-full w-full -rotate-90">
        <circle cx="42" cy="42" r="34" fill="none" stroke="rgba(168,184,232,0.16)" strokeWidth="5" />
        <motion.circle
          cx="42" cy="42" r="34" fill="none" stroke="#F6E9C8" strokeWidth="5" strokeLinecap="round"
          strokeDasharray={C}
          initial={{ strokeDashoffset: C }}
          whileInView={{ strokeDashoffset: C * 0.18 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ delay: 0.25, duration: 1.1, ease: [0.22, 1, 0.36, 1], repeat: Infinity, repeatType: 'mirror', repeatDelay: 2.2 }}
        />
      </svg>
      <motion.span
        className="absolute inset-0 grid place-items-center font-serif text-[30px] font-semibold"
        style={{ color: '#DFC993' }}
        initial={{ scale: 0.4, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        viewport={{ once: true, amount: 0.6 }}
        transition={{ delay: 1.05, type: 'spring', stiffness: 260, damping: 13, repeat: Infinity, repeatType: 'mirror', repeatDelay: 2.2 }}
      >
        7
      </motion.span>
      {SPARKS.map((sp, i) => (
        <motion.span
          key={i}
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-1/2 text-[11px]"
          style={{ color: '#F6E9C8' }}
          initial={{ x: 0, y: 0, opacity: 0, scale: 0.4 }}
          whileInView={{ x: sp.x, y: sp.y, opacity: [0, 1, 0], scale: [0.4, 1, 0.6] }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ delay: 1.1 + sp.d, duration: 1.0, ease: 'easeOut', repeat: Infinity, repeatType: 'loop', repeatDelay: 2.9 }}
        >
          ✦
        </motion.span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ceremonies — the exported section
// ---------------------------------------------------------------------------
export function Ceremonies() {
  const { t } = useTranslation();
  const reduced = !!useReducedMotion();
  const stamp = t('landing.ceremonies.dossierStamp', 'arquivado');

  return (
    <section className="relative overflow-hidden bg-background py-16 md:py-24" id="ceremonies">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className="mx-auto mb-10 max-w-2xl text-center md:mb-14"
        >
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            <span className="text-accent">✦</span> {t('landing.ceremonies.eyebrow', 'as cerimônias')}
          </span>
          <h2 className="mt-4 font-serif text-3xl text-primary md:text-4xl">
            {t('landing.ceremonies.title', 'Pequenos rituais, grandes histórias.')}
          </h2>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground md:text-lg">
            {t(
              'landing.ceremonies.subtitle',
              'Cada meta, cada personagem, cada noite de escrita vira um momento — não apenas uma barra de progresso.',
            )}
          </p>
        </motion.div>

        <div className="ceremony-grid grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <CeremonyFrame
            index={0}
            eyebrow={t('landing.ceremonies.candleEyebrow', 'a vela do dia')}
            title={t('landing.ceremonies.candleTitle', 'Bata a meta, acenda a vela.')}
            caption={t('landing.ceremonies.candleCaption', 'Cada meta cumprida acende uma chama que só você vê.')}
          >
            <CandleScene reduced={reduced} />
          </CeremonyFrame>

          <CeremonyFrame
            index={1}
            eyebrow={t('landing.ceremonies.constellationEyebrow', 'a constelação da semana')}
            title={t('landing.ceremonies.constellationTitle', 'Sete noites, uma constelação.')}
            caption={t('landing.ceremonies.constellationCaption', 'Cada dia de escrita vira uma estrela no seu céu pessoal.')}
          >
            <ConstellationScene reduced={reduced} />
          </CeremonyFrame>

          <CeremonyFrame
            index={2}
            eyebrow={t('landing.ceremonies.dossierEyebrow', 'o dossiê do personagem')}
            title={t('landing.ceremonies.dossierTitle', 'Datilografado. Carimbado. Seu.')}
            caption={t('landing.ceremonies.dossierCaption', 'Cada personagem ganha uma ficha própria — como um arquivo secreto.')}
          >
            <DossierScene reduced={reduced} name={t('landing.ceremonies.dossierName', 'Helena Duarte')} stamp={stamp} />
          </CeremonyFrame>

          <CeremonyFrame
            index={3}
            eyebrow={t('landing.ceremonies.levelEyebrow', 'progresso que se sente')}
            title={t('landing.ceremonies.levelTitle', 'Escreva. Suba de nível. Repita.')}
            caption={t('landing.ceremonies.levelCaption', 'Cada marco vira uma pequena conquista, faíscas incluídas.')}
          >
            <LevelUpScene reduced={reduced} />
          </CeremonyFrame>
        </div>
      </div>
    </section>
  );
}
